import { formatLedgerMoney } from '@/lib/api/accounts'
import {
  formatPaymentMixLabel,
  formatZReportDateTime,
  normalizePaymentMix,
} from '@/lib/accounts/zReport'

/**
 * @param {object} props
 * @param {object | null} props.report
 * @param {{ name?: string, trn?: string } | null} [props.branch]
 */
export function ZReportPrint({ report, branch }) {
  if (!report) return null

  const paymentLines = normalizePaymentMix(report.payment_mix)
  const cashIn = report.cash_in ?? {
    opening_float: report.opening_float,
    cash_sales: null,
  }
  const cashOut = report.cash_out ?? {
    cash_refunds: null,
    cash_expenses: report.cash_expenses_total,
    expenses_by_method: {},
  }
  const expenseMethodLines = normalizePaymentMix(cashOut.expenses_by_method)

  return (
    <div className="thermal-receipt-root pointer-events-none fixed -left-[9999px] top-0">
      <article className="thermal-receipt" aria-label="Z-report">
        <header className="thermal-receipt__header">
          <p className="thermal-receipt__title">{branch?.name ?? 'Salon branch'}</p>
          {branch?.trn ? <p>TRN: {branch.trn}</p> : null}
          <p>Z-REPORT</p>
        </header>

        <section className="thermal-receipt__meta">
          <Row label="Report" value={report.report_number ?? ''} />
          <Row label="Business date" value={report.business_date ?? ''} />
          <Row label="Generated" value={formatZReportDateTime(report.generated_at)} />
        </section>

        <section className="thermal-receipt__lines">
          <Row label="Gross sales" value={formatLedgerMoney(report.gross_sales)} />
          <Row label="Discounts" value={formatLedgerMoney(report.discount_total)} />
          <Row label="VAT" value={formatLedgerMoney(report.vat_total)} />
          <Row label="Net sales" value={formatLedgerMoney(report.net_sales)} />
          <Row label="Voids" value={`${report.void_count ?? 0} / ${formatLedgerMoney(report.void_amount)}`} />
        </section>

        <section className="thermal-receipt__totals">
          <p className="thermal-receipt__stylist">Payment mix (in)</p>
          {paymentLines.length === 0 ? (
            <Row label="None" value={formatLedgerMoney(0)} />
          ) : (
            paymentLines.map((line) => (
              <Row key={line.code} label={line.label} value={line.amount} />
            ))
          )}
        </section>

        <section className="thermal-receipt__totals">
          <p className="thermal-receipt__stylist">Cash in</p>
          <Row label="Opening float" value={formatLedgerMoney(cashIn.opening_float)} />
          {cashIn.cash_sales != null ? (
            <Row label="Cash sales" value={formatLedgerMoney(cashIn.cash_sales)} />
          ) : null}
        </section>

        <section className="thermal-receipt__totals">
          <p className="thermal-receipt__stylist">Cash out</p>
          {cashOut.cash_refunds != null ? (
            <Row label="Cash refunds" value={formatLedgerMoney(cashOut.cash_refunds)} />
          ) : null}
          {cashOut.cash_expenses != null ? (
            <Row label="Cash expenses" value={formatLedgerMoney(cashOut.cash_expenses)} />
          ) : report.cash_expenses_total != null ? (
            <Row label="Cash expenses" value={formatLedgerMoney(report.cash_expenses_total)} />
          ) : null}
          {expenseMethodLines.length > 0 ? (
            <>
              <p className="thermal-receipt__stylist">Expenses by method</p>
              {expenseMethodLines.map((line) => (
                <Row key={`exp-${line.code}`} label={line.label} value={line.amount} />
              ))}
            </>
          ) : null}
        </section>

        <section className="thermal-receipt__totals">
          <Row label="Expected cash" value={formatLedgerMoney(report.closing_cash_expected)} />
          <Row label="Counted cash" value={formatLedgerMoney(report.closing_cash_counted)} />
          <div className="thermal-receipt__row thermal-receipt__total">
            <span>Variance</span>
            <span>{formatLedgerMoney(report.cash_variance)}</span>
          </div>
        </section>

        {Array.isArray(report.expenses) && report.expenses.length > 0 ? (
          <section className="thermal-receipt__lines">
            <p className="thermal-receipt__stylist">Expense lines</p>
            {report.expenses.map((row) => (
              <Row
                key={row.id}
                label={`${row.category}${row.note ? ` (${row.note})` : ''} · ${formatPaymentMixLabel(row.payment_method)}`}
                value={formatLedgerMoney(row.amount)}
              />
            ))}
          </section>
        ) : null}
      </article>
    </div>
  )
}

/**
 * On-screen payment mix + cash in/out summary for Cash day.
 *
 * @param {object} props
 * @param {object | null | undefined} props.report
 * @param {string} [props.className]
 */
export function ZReportMoneySummary({ report, className }) {
  if (!report) return null

  const paymentLines = normalizePaymentMix(report.payment_mix)
  const cashIn = report.cash_in
  const cashOut = report.cash_out
  const expenseMethodLines = normalizePaymentMix(cashOut?.expenses_by_method)

  return (
    <div className={className}>
      {paymentLines.length > 0 ? (
        <div className="mb-3">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Payment mix</p>
          <ul className="space-y-1 text-sm">
            {paymentLines.map((line) => (
              <li key={line.code} className="flex justify-between gap-3">
                <span className="text-slate-600">{line.label}</span>
                <span className="font-medium text-slate-900">AED {line.amount}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {cashIn ? (
        <div className="mb-3">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Cash in</p>
          <ul className="space-y-1 text-sm">
            <li className="flex justify-between gap-3">
              <span className="text-slate-600">Opening float</span>
              <span className="font-medium">AED {formatLedgerMoney(cashIn.opening_float)}</span>
            </li>
            <li className="flex justify-between gap-3">
              <span className="text-slate-600">Cash sales</span>
              <span className="font-medium">AED {formatLedgerMoney(cashIn.cash_sales)}</span>
            </li>
          </ul>
        </div>
      ) : null}

      {cashOut ? (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Cash out</p>
          <ul className="space-y-1 text-sm">
            <li className="flex justify-between gap-3">
              <span className="text-slate-600">Cash refunds</span>
              <span className="font-medium">AED {formatLedgerMoney(cashOut.cash_refunds)}</span>
            </li>
            <li className="flex justify-between gap-3">
              <span className="text-slate-600">Cash expenses</span>
              <span className="font-medium">AED {formatLedgerMoney(cashOut.cash_expenses)}</span>
            </li>
            {expenseMethodLines.map((line) => (
              <li key={`exp-${line.code}`} className="flex justify-between gap-3">
                <span className="text-slate-600">Expenses · {line.label}</span>
                <span className="font-medium">AED {line.amount}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.value
 */
function Row({ label, value }) {
  return (
    <div className="thermal-receipt__row">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}
